/* @flow
 */
import * as R from "ramda";
import config from "../../../core/config";
import type { Action } from "../../../types";

type State = {
  ui: {
    tokenExpired: boolean,
    open: boolean,
    isRequestingMe: boolean,
    fetchingNotifications: boolean,
    isRequestingLive: boolean,
    isErrorReport: boolean,
    maskSearch: boolean,
    hasError: boolean
  },
  data: {
    notifications: Array<mixed>,
    live: Array<mixed>,
    errorReport: Array<mixed>,
    user: {
      name: string,
      email: string
    },
    helmet: Object
  }
};

const emptyUser = {
  name: "Jubialuno (a)",
  email: ""
};

const emptyHelmet = {
  title: "Aprova Total",
  canonical: "https://www.aprovatotal.com.br",
  pathname: ""
};

const initialState = {
  buildNumber: config.buildNumber,
  ui: {
    tokenExpired: false,
    open: true,
    isRequestingMe: true,
    fetchingNotifications: false,
    isRequestingLive: false,
    isErrorReport: false,
    maskSearch: false,
    notificationsLoadMore: true,
    hasError: false
  },
  data: {
    user: { ...emptyUser },
    notifications: [],
    errorReport: [],
    live: [],
    helmet: { ...emptyHelmet }
  }
};

function getFullPathFromCanonical(data) {
  if (!data.title || !data.canonical) {
    return {
      ...emptyHelmet
    };
  } else {
    return {
      ...data,
      title: `${data.title} - Aprova Total`,
      canonical: `${config.baseURL}${data.canonical}`,
      pathname: data.canonical
    };
  }
}

function appReducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case "APP/CLOSE_LIVE_NOTIFICATION": {
      const lens = R.lensPath(["data", "live"]);
      const lives = state.data.live;

      const newLives = lives.map(it => (it.event.slug === action.slug ? { ...it, close: true } : it));

      return R.set(lens, newLives, state);
    }
    case "APP/ERROR_FOUND": {
      const lens = R.lensPath(["ui", "hasError"]);
      return R.set(lens, true, state);
    }
    case "APP/NEW_LOGIN": {
      const lens = R.lensPath(["ui", "tokenExpired"]);
      return R.set(lens, false, state);
    }
    case "APP/TOKEN_EXPIRED": {
      window.location = "/management/login";
      const lens = R.lensPath(["ui", "tokenExpired"]);
      return R.set(lens, true, state);
    }
    case "APP/LOGOUT": {
      const lens = R.lensPath(["data", "user"]);
      return R.set(lens, { ...emptyUser }, state);
    }
    case "APP/TOGGLE_MENU": {
      const fetchLens = R.lensPath(["ui", "open"]);
      return R.set(fetchLens, !state.ui.open, state);
    }
    case "APP/MASK_SEARCH": {
      const fetchLens = R.lensPath(["ui", "maskSearch"]);
      return R.set(fetchLens, action.data, state);
    }
    case "APP/REQUEST_ME": {
      const lens = R.lensPath(["ui", "isRequestingMe"]);
      return R.set(lens, true, state);
    }

    case "APP/REQUEST_ME_SUCCESS": {
      const lens = R.lensPath(["ui", "isRequestingMe"]);
      const lensData = R.lensPath(["data", "user"]);
      return R.compose(R.set(lens, false), R.set(lensData, action.data))(state);
    }
    case "APP/REQUEST_ME_FAILURE":
    case "APP/REQUEST_ME_ERROR": {
      const lens = R.lensPath(["ui", "isRequestingMe"]);
      return R.set(lens, false, state);
    }

    case "APP/REQUEST_LIVE_SUCCESS": {
      const lens = R.lensPath(["ui", "isRequestingLive"]);
      const lensData = R.lensPath(["data", "live"]);
      return R.compose(R.set(lens, false), R.set(lensData, action.data))(state);
    }
    case "APP/REQUEST_LIVE":
    case "APP/REQUEST_LIVE_FAILURE": {
      const lens = R.lensPath(["ui", "isRequestingLive"]);
      return R.set(lens, false, state);
    }

    case "APP/NOTIFICATIONS_REQUEST": {
      const fetchLens = R.lensPath(["ui", "fetchingNotifications"]);
      return R.set(fetchLens, true, state);
    }
    case "APP/NOTIFICATIONS_SUCCESS": {
      const dataLens = R.lensPath(["data", "notifications"]);
      const fetchLens = R.lensPath(["ui", "fetchingNotifications"]);
      const fetchLens1 = R.lensPath(["ui", "notificationsLoadMore"]);

      let newNotifications = action.data;
      let notifications = state.data.notifications;
      if (action.data) {
        notifications = [...notifications, ...newNotifications];
      }
      let notificationsLoadMore = newNotifications.length < 5 ? false : true;

      return R.compose(
        R.set(dataLens, notifications),
        R.set(fetchLens, false),
        R.set(fetchLens1, notificationsLoadMore)
      )(state);
    }
    case "APP/NOTIFICATIONS_FAILURE": {
      const dataLens = R.lensPath(["data", "notifications"]);
      const fetchLens = R.lensPath(["ui", "fetchingNotifications"]);

      return R.compose(R.set(dataLens, []), R.set(fetchLens, false))(state);
    }

    case "APP/NOTIFICATIONS_HISTORY_REQUEST": {
      const fetchLens = R.lensPath(["ui", "fetchingNotificationsHistory"]);
      return R.set(fetchLens, true, state);
    }
    case "APP/NOTIFICATIONS_HISTORY_SUCCESS": {
      const dataLens = R.lensPath(["data", "notifications"]);
      const fetchLens = R.lensPath(["ui", "fetchingNotificationsHistory"]);

      let newNotifications = [];
      if (action.data.ok) {
        const notifications = state.data.notifications;
        newNotifications = notifications.map(it => {
          it.seenDate = new Date().toISOString();
          return it;
        });
      }

      return R.compose(R.set(dataLens, newNotifications), R.set(fetchLens, false))(state);
    }
    case "APP/NOTIFICATIONS_HISTORY_FAILURE": {
      const fetchLens = R.lensPath(["ui", "fetchingNotificationsHistory"]);

      return R.compose(R.set(fetchLens, false))(state);
    }

    case "APP/HELMET_REQUEST": {
      const helmetLens = R.lensPath(["data", "helmet"]);

      return R.set(helmetLens, { ...getFullPathFromCanonical(action.data) }, state);
    }

    case "APP/ERROR_REPORT_SUCCESS": {
      const lens = R.lensPath(["ui", "isErrorReport"]);
      const lensData = R.lensPath(["data", "errorReport"]);
      return R.compose(R.set(lens, false), R.set(lensData, action.data))(state);
    }
    case "APP/ERROR_REPORT_REQUEST":
    case "APP/ERROR_REPORT_FAILURE": {
      const lens = R.lensPath(["ui", "isErrorReport"]);
      return R.set(lens, false, state);
    }

    case "APP/CLEAN_STATE": {
      return initialState;
    }

    default:
      return state;
  }
}

export default appReducer;
